import cx from 'classnames'
import { useCallback, useRef, useState } from 'react'
import Styles from './Typeahead.module.scss'

import { useOutsideClick } from 'components/Comments/hooks/useOutSideClick'
import { MentionPrompt } from 'proseShared/prompts/MentionPrompt/MentionPrompt'
import { ReactComponent as SearchIcon } from './icons/search.svg'
import type { TypeaheadProps } from './Types'

const Typeahead = ({
  initialQuery,
  onChange,
  onCancel,
  mentioningArticle,
  onSelect,
  allowCreateNewFile,
  suggestRecentlyReadArticle,
  dropdownWidth,
  overrideMentionPromptClassname,
}: TypeaheadProps) => {
  const [query, setQuery] = useState(initialQuery)
  const placeholder = 'Search for a person or doc...'
  const empty = query === ''
  const spacerText = empty ? placeholder : query
  const containerRef = useRef(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const focusInput = useCallback((inputElement: any) => {
    if (inputElement) {
      setTimeout(() => {
        inputElement.focus()
        inputElement.select()
      }, 50)

      inputRef.current = inputElement
    }
  }, [])

  useOutsideClick(containerRef, onCancel)

  return (
    <div ref={containerRef}>
      <div className={Styles.typeaheadInputWrapper}>
        <div className={Styles.typeaheadResizingInput}>
          {empty ? (
            <div className={Styles.emptyState}>
              <SearchIcon className={Styles.typeaheadInputIcon} />
              <div className={Styles.placeholderText}>{placeholder}</div>
            </div>
          ) : (
            <span className={Styles.typeaheadHiddenText}>{spacerText}</span>
          )}
          <input
            key="typeaheadInput"
            ref={focusInput}
            className={cx(Styles.typeaheadInput, { [Styles.typeaheadInputEmptyState]: empty })}
            type="text"
            value={query}
            autoFocus
            onChange={(e) => {
              setQuery(e.target.value)
              onChange(e.target.value)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape' || (event.key === ' ' && empty)) {
                onCancel()
              }
            }}
          />
        </div>
      </div>
      <div className={Styles.floatingPrompt} data-testid="typeaheadFloatingPrompt">
        <MentionPrompt
          inputQuery={query}
          mentioningArticle={mentioningArticle}
          suggestRecentlyReadArticle={suggestRecentlyReadArticle}
          allowCreateNewFile={allowCreateNewFile}
          onSelect={onSelect}
          dropdownWidth={dropdownWidth}
          overrideContainerClassname={overrideMentionPromptClassname}
          onKeyDown={() => {
            inputRef.current?.focus()
          }}
        />
      </div>
    </div>
  )
}

export { Typeahead }
